<template>
  <v-card class="mb-2 card">
    <div class="section">
      <div class="names">
        <Upload
          @add="onAddFile"
          container="profilbilder"
          :hide-container="true"
          :add-timestamp="true"
          accept="image/*"
        >
          <div
            v-if="contact.contact_profile_image"
            class="profile-image"
            :style="profileImageStyleUrl"
          ></div>
          <v-btn v-else icon style="background: lightblue">
            <v-icon>mdi-account</v-icon>
          </v-btn>
        </Upload>
        <InPlaceEditor :show-label="false" label="First name" v-model="firstName" size="big" />
        <InPlaceEditor :show-label="false" label="Last name" v-model="lastName" size="big" />
        <v-icon
          title="Ny kontakt"
          @click="onChangeStatus"
          class="star"
          small
          :color="contact.contact_status === 'new' ? 'yellow' : '#ccc'"
          >mdi-star</v-icon
        >
      </div>

      <InPlaceEditor label="Organisation" v-model="organisation" />
      <InPlaceEditor label="Avdelning" v-model="unit" />
      <InPlaceEditor label="Adress" v-model="adress" />
      <InPlaceEditor label="Postnummer" v-model="zip" />
      <InPlaceEditor label="Ort" v-model="city" />
      <InPlaceEditor label="Land" v-model="country" />
    </div>
    <div class="section">
      <InPlaceEditor label="Organisationstyp" v-model="orgType" />
      <InPlaceEditor label="Telefon" v-model="telephone" />
      <InPlaceEditor label="Mobil" v-model="mobile" />
      <InPlaceEditor label="Epost" v-model="email" />
      <InPlaceEditor label="Hemsida" v-model="www" />
    </div>
    <div class="section">
      <InPlaceEditor label="Presentation" v-model="presentation" />
      <InPlaceEditor label="Interna kommentarer" v-model="internalComments" />
      <InPlaceEditor label="Sökord" v-model="keywords" />
      <div class="subtitle-2 mt-2">Taggar</div>
      <div class="tags-container">
        <v-chip
          v-for="tag in contactTags"
          :title="tag.description"
          :key="tag.id"
          small
          close
          :color="getTagColor(tag.color)"
          label
          text-color="white"
          class="tags"
          @click:close="onRemoveTag(tag)"
        >
          {{ tag.name }}
        </v-chip>
        <AddTag
          :allTags="tags"
          :selectedTags="contactTags"
          @add-tag="onAddTag"
          @remove-tag="onDeleteTag"
        />
      </div>
    </div>
    <div></div>
    <div></div>
    <div class="meta-row">
      <span>Senast uppdaterad {{ updatedDate }} av {{ updatedBy }}</span>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn x-small :loading="deletingLoading" icon color="#aaa" v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-horizontal</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item dense link @click="onDeleteContact">
            <v-list-item-title>Radera kontakt</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <Confirm ref="confirm" />
    <Message ref="message" />
  </v-card>
</template>

<script>
import InPlaceEditor from '@/components/shared/InplaceEditor.vue'
import AddTag from '@/components/contacts/AddTag.vue'
import { getTagColor } from '@/assets/data/colors.js'
import { dateParse } from '../../lib/misc'
import { mapGetters, mapActions } from 'vuex'
import Confirm from '@/components/shared/Confirm'
import Message from '@/components/shared/Message'
import Upload from '@/components/shared/Upload.vue'

export default {
  name: 'ContactCard',
  props: {
    contact: Object,
    tags: Array,
  },
  components: {
    InPlaceEditor,
    AddTag,
    Confirm,
    Message,
    Upload,
  },
  data() {
    return {
      deletingLoading: false,
      uploadFile: null,
    }
  },
  computed: {
    ...mapGetters('user', { getUserNameById: 'getUserNameById' }),
    profileImageStyleUrl() {
      return `background-image:url('${this.contact.contact_profile_image}')`
    },
    createdDate() {
      return dateParse(this.contact.created_at)
    },
    updatedDate() {
      return dateParse(this.contact.updated_at)
    },
    updatedBy() {
      return this.getUserNameById(this.contact.updated_by)
    },
    firstName: {
      get() {
        return this.contact.contact_first_name ? this.contact.contact_first_name.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_first_name: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    lastName: {
      get() {
        return this.contact.contact_last_name ? this.contact.contact_last_name.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_last_name: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    organisation: {
      get() {
        return this.contact.contact_organisation ? this.contact.contact_organisation.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_organisation: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    unit: {
      get() {
        return this.contact.contact_unit ? this.contact.contact_unit.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_unit: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    adress: {
      get() {
        return this.contact.contact_address ? this.contact.contact_address.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_address: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    zip: {
      get() {
        return this.contact.contact_zip ? this.contact.contact_zip.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_zip: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    city: {
      get() {
        return this.contact.contact_city ? this.contact.contact_city.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_city: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    country: {
      get() {
        return this.contact.contact_country ? this.contact.contact_country.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_country: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    orgType: {
      get() {
        return this.contact.contact_org_type ? this.contact.contact_org_type.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_org_type: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    telephone: {
      get() {
        return this.contact.contact_tel ? this.contact.contact_tel.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_tel: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    mobile: {
      get() {
        return this.contact.contact_mob ? this.contact.contact_mob.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_mob: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    email: {
      get() {
        return this.contact.contact_mail
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_mail: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    www: {
      get() {
        return this.contact.contact_www
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_www: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    presentation: {
      get() {
        return this.contact.contact_more_info
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_more_info: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    internalComments: {
      get() {
        return this.contact.contact_internal_comments
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_internal_comments: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    keywords: {
      get() {
        return this.contact.contact_keywords ? this.contact.contact_keywords.trim() : ''
      },
      set(val) {
        const updatedContact = { ...this.contact, contact_keywords: val }
        this.$emit('update-contact', updatedContact)
      },
    },
    contactTags() {
      if (!this.contact.contact_tags) return []
      return this.contact.contact_tags.map((t) => {
        return { ...this.tags.find((tag) => tag.id === t.tagId), ...t }
      })
    },
  },
  methods: {
    ...mapActions('contacts', { deleteContact: 'delete' }),
    async onAddFile({ azureFile }) {
      this.uploadFile = azureFile
      const { status, url } = await this.uploadFile.send()
      if (status === 201) {
        const updatedContact = { ...this.contact, contact_profile_image: url }
        this.$emit('update-contact', updatedContact)
      } else {
        // eslint-disable-next-line no-alert
        alert('Problem uppstod när filen skulle laddas upp')
      }
      this.uploadFile = null
    },
    getTagColor,
    onChangeStatus() {
      const updatedContact = {
        ...this.contact,
        contact_status: this.contact.contact_status === 'new' ? '' : 'new',
      }
      this.$emit('update-contact', updatedContact)
    },
    onRemoveTag(tag) {
      this.$emit('remove-tag', tag)
    },
    onDeleteTag(tag) {
      this.$emit('remove-tag', { tagId: tag.id, contactId: this.contact.id })
    },
    onAddTag(tag) {
      this.$emit('add-tag', { tagId: tag.id, contactId: this.contact.id })
    },
    async onDeleteContact() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort kontakten',
        message: 'Är du säker på att du vill ta bort kontakten?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return
      this.deletingLoading = true
      await this.deleteContact(this.contact)
      this.deletingLoading = true
      this.$refs.message.open('Kontakten har tagits bort')
    },
  },
}
</script>

<style lang="less" scoped>
.card {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 25px;
  grid-column-gap: 5px;
  grid-row-gap: 0px;
  padding: 10px 15px;
}
.meta-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  font-size: 12px;
  color: #999;
}
.names {
  display: flex;
}
.section {
  max-width: 100%;
  overflow: auto;
}
.tags {
  margin-right: 4px;
  margin-bottom: 4px;
}
.star {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.star:hover {
  transform: scale(1.2);
}

.profile-image {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
}
</style>
